import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { clearAccessToken, clearRefreshToken, getAccessToken, getRefreshToken, setAccessToken, setRefreshToken, getApiBaseUrl } from '@safe-minds-ai/utils';

interface SafeAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

let client: AxiosInstance | null = null;
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(http: AxiosInstance): Promise<string | null> {
  if (!refreshPromise) {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      forceLogout();
      return null;
    }
    refreshPromise = http
      .post<{ access_token?: string; accessToken?: string; refresh_token?: string; refreshToken?: string }>(
        '/auth/refresh',
        {},
        {
          headers: {
            'x-refresh-token': refreshToken,
          },
        }
      )
      .then((response) => {
        const token = response.data?.access_token ?? response.data?.accessToken;
        const nextRefresh = response.data?.refresh_token ?? response.data?.refreshToken;
        if (token) {
          setAccessToken(token);
          if (nextRefresh) {
            setRefreshToken(nextRefresh);
          }
          return token;
        }
        forceLogout();
        return null;
      })
      .catch(() => {
        forceLogout();
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

export function getHttpClient(): AxiosInstance {
  if (client) {
    return client;
  }

  const baseURL = getApiBaseUrl();

  client = axios.create({
    baseURL,
    withCredentials: true,
  });

  client.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (typeof window !== 'undefined') {
      const offset = new Date().getTimezoneOffset();
      config.headers = {
        ...(config.headers ?? {}),
        'x-client-tz-offset': String(offset),
      };
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const response = error.response;
      const originalRequest = error.config as SafeAxiosRequestConfig | undefined;

      if (response?.status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        const token = await refreshAccessToken(client!);
        if (token) {
          originalRequest.headers = {
            ...(originalRequest.headers ?? {}),
            Authorization: `Bearer ${token}`,
          };
          return client!.request(originalRequest);
        }

        forceLogout();
        return Promise.reject(error);
      }

      if (response?.status === 401) {
        forceLogout();
      }

      return Promise.reject(error);
    }
  );

  return client;
}

export function resetHttpClient(): void {
  client = null;
}
function forceLogout() {
  clearAccessToken();
  clearRefreshToken();
  if (typeof window !== 'undefined') {
    const current = window.location.pathname;
    if (!current.startsWith('/login')) {
      window.location.href = '/login';
    } else {
      window.location.reload();
    }
  }
}
